import { useCallback, useState } from 'react';
import {
  useController,
  type Control,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { Box, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { EdtBadge } from '~/commonComponents/EdtBadge';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { ShiftRequestRateFriction } from '~/constants/ff.posthog';
import { UserProfession } from '~/enums/UserProfession';
import { useFeatureFlagVariant } from '~/hooks/useFeatureFlagVariant';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useUserSelector } from '~/hooks/useUserSelector';
import { getIsAllowOfficeToPostProcedure } from '~/utils/DentistUtils';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { AssistedHygieneField } from './fields/AssistedHygieneField';
import { ExpectedProceduresField } from './fields/ExpectedProcedures';
import { MealBreakDurationField } from './fields/MealBreakDurationField';
import { MealBreakPaidField } from './fields/MealBreakPaidField';
import { OfficeField } from './fields/OfficeField';
import { PreferredSpecialtyField } from './fields/PreferredSpecialtyField';
import { ProfessionalPayField } from './fields/ProfessionalPayField';
import { RateSelection } from './fields/RateSelection';
import { ProfessionalTypeField } from './fields/ProfessionalTypeField';
import { TimeFieldsWrapper } from './fields/TimeFieldsWrapper';
import { useProfessionTypeOptions } from './hooks/useProfessionTypeOptions';
import { type IShiftDetailsFormValues } from './useShiftDetailsForm';

interface IShiftDetailsFormProps {
  control: Control<IShiftDetailsFormValues>;
  watch: UseFormWatch<IShiftDetailsFormValues>;
  setValue: UseFormSetValue<IShiftDetailsFormValues>;
  /** When true, empty required fields are highlighted with a red outline */
  showFieldErrors?: boolean;
}

export const ShiftDetailsForm = ({
  control,
  watch,
  setValue,
  showFieldErrors = false,
}: IShiftDetailsFormProps) => {
  const professionType = watch('professionType');
  const preferredSpecialty = watch('preferredSpecialty');
  const invite = watch('invite');
  const dates = watch('dates');
  const arrivalTime = watch('arrivalTime');
  const departureTime = watch('departureTime');
  const mealBreakDuration = watch('mealBreakDuration');
  const rate = watch('rate');
  const isAssistedHygiene = watch('isAssistedHygiene');
  const professionCode = professionType ? UserProfession.getCode(professionType) : '';
  const isRDH = professionCode === UserProfession.RDH;
  const showProcedures = getIsAllowOfficeToPostProcedure(professionType, preferredSpecialty);

  const { field: officeIdField } = useController({ name: 'officeId', control });
  const { field: professionTypeField } = useController({ name: 'professionType', control });
  const { field: preferredSpecialtyField } = useController({ name: 'preferredSpecialty', control });
  const { field: isAssistedHygieneField } = useController({ name: 'isAssistedHygiene', control });
  const { field: arrivalField } = useController({ name: 'arrivalTime', control });
  const { field: departureField } = useController({ name: 'departureTime', control });
  const { field: mealBreakDurationField } = useController({ name: 'mealBreakDuration', control });
  const { field: isMealBreakPaidField } = useController({ name: 'isMealBreakPaid', control });
  const { field: rateField } = useController({ name: 'rate', control });

  const professionOptions = useProfessionTypeOptions(officeIdField.value);
  // Experiment: only the "test" variant gets the rate-friction stepper; "control"
  // (and anyone not enrolled) keeps the legacy rate dropdown.
  const isRateFrictionEnabled = useFeatureFlagVariant(ShiftRequestRateFriction, 'test');

  const currentUser = useUserSelector();
  const childOffices = useAppSelector((state) => state.user.childOffices);

  const resetProfessionIfInvalid = useCallback(
    (newOfficeId: string) => {
      if (!professionType) return;

      const office =
        currentUser?.id === newOfficeId
          ? currentUser
          : childOffices?.find((o) => o.id === newOfficeId);

      const officeState = office?.address?.[0]?.state;
      if (!officeState) return;

      const professionsByState = RemoteConfigUtils.getProfessionsByState();
      const stateProfessions = professionsByState[officeState];
      if (!stateProfessions || stateProfessions.length === 0) return;

      const currentCode = UserProfession.getCode(professionType as string);
      if (!currentCode || !stateProfessions.includes(currentCode)) {
        setValue('professionType', '');
        setValue('preferredSpecialty', '');
        setValue('procedures', []);
        setValue('isAssistedHygiene', false);
      }
    },
    [professionType, currentUser, childOffices, setValue],
  );

  const [showOfficeChangeWarning, setShowOfficeChangeWarning] = useState(false);
  const [pendingOfficeId, setPendingOfficeId] = useState<string | null>(null);
  const [showProfessionMismatchWarning, setShowProfessionMismatchWarning] = useState(false);

  const handleOfficeChange = (newOfficeId: string) => {
    if (invite.length > 0 && officeIdField.value && newOfficeId !== officeIdField.value) {
      setPendingOfficeId(newOfficeId);
      setShowOfficeChangeWarning(true);
      return;
    }
    officeIdField.onChange(newOfficeId);
    resetProfessionIfInvalid(newOfficeId);
  };

  const handleProfessionTypeChange = (newProfessionType: string) => {
    if (invite.length > 0 && newProfessionType !== professionTypeField.value) {
      setShowProfessionMismatchWarning(true);
      return;
    }
    professionTypeField.onChange(newProfessionType);
  };

  return (
    <Column data-testid="shift-details-form" data-cy="shift-details-form" gap={5}>
      <OfficeField value={officeIdField.value} onChange={handleOfficeChange} />
      <ProfessionalTypeField
        value={professionTypeField.value}
        onChange={handleProfessionTypeChange}
        professionOptions={professionOptions}
        hasError={showFieldErrors && !professionType}
      />
      <Box style={{ height: '1.5px', backgroundColor: colors.neutral[100] }} />
      <PreferredSpecialtyField
        value={preferredSpecialtyField.value}
        onChange={preferredSpecialtyField.onChange}
        professionType={professionType}
        hasError={showFieldErrors && !preferredSpecialty}
      />
      {showProcedures && (
        <>
          <Box style={{ height: '1.5px', backgroundColor: colors.neutral[100] }} />
          <ExpectedProceduresField
            control={control}
            professionType={professionType}
            preferredSpecialty={preferredSpecialty}
          />
          <Box style={{ height: '1.5px', backgroundColor: colors.neutral[100] }} />
        </>
      )}
      {isRDH && (
        <AssistedHygieneField
          value={isAssistedHygieneField.value}
          onChange={isAssistedHygieneField.onChange}
          hasError={showFieldErrors && isAssistedHygiene === undefined}
        />
      )}

      <Column gap={3} style={{ marginTop: 16 }}>
        <Typography as="span" bold size="heading_3" color={colors.neutral[600]}>
          {isRateFrictionEnabled ? 'Hours' : 'Hours & Pay'}
        </Typography>
        <Box style={{ height: '1.5px', backgroundColor: colors.neutral[100] }} />
      </Column>

      <TimeFieldsWrapper
        arrivalValue={arrivalField.value}
        onArrivalChange={arrivalField.onChange}
        departureValue={departureField.value}
        onDepartureChange={departureField.onChange}
        labelRight={<EdtBadge />}
        hasArrivalError={showFieldErrors && !arrivalTime}
        hasDepartureError={showFieldErrors && !departureTime}
      />

      <Row style={{ gap: 30 }}>
        <Column style={{ flex: 1 }}>
          <MealBreakDurationField
            value={mealBreakDurationField.value}
            onChange={mealBreakDurationField.onChange}
            hasError={showFieldErrors && !mealBreakDuration}
          />
        </Column>
        <Column style={{ flex: 1 }}>
          <MealBreakPaidField
            value={isMealBreakPaidField.value}
            onChange={isMealBreakPaidField.onChange}
          />
        </Column>
      </Row>

      {isRateFrictionEnabled ? (
        <RateSelection
          value={rateField.value}
          onChange={rateField.onChange}
          professionType={professionType}
          officeId={officeIdField.value}
          hasError={showFieldErrors && rate === null}
          dates={dates}
          arrivalTime={arrivalTime}
        />
      ) : (
        <ProfessionalPayField
          value={rateField.value}
          onChange={rateField.onChange}
          professionType={professionType}
          officeId={officeIdField.value}
          hasError={showFieldErrors && rate === null}
          dates={dates}
          arrivalTime={arrivalTime}
        />
      )}

      {showOfficeChangeWarning && (
        <ConfirmPopup
          title="Confirm Office Change"
          description="Changing the office will clear the professionals you've selected to invite for this shift. Do you want to continue?"
          leftButtonText="Go Back"
          leftButtonAction={() => {
            setPendingOfficeId(null);
            setShowOfficeChangeWarning(false);
          }}
          rightButtonText="Continue"
          rightButtonAction={() => {
            officeIdField.onChange(pendingOfficeId);
            setValue('invite', []);
            setValue('inviteNames', {});
            if (pendingOfficeId) {
              resetProfessionIfInvalid(pendingOfficeId);
            }
            setPendingOfficeId(null);
            setShowOfficeChangeWarning(false);
          }}
          closePopup={() => setShowOfficeChangeWarning(false)}
          dataTestId="office-change-popup"
          descriptionStyle={{
            width: '85%',
            marginBottom: 32,
          }}
        />
      )}

      {showProfessionMismatchWarning && (
        <ConfirmPopup
          title="Professional Doesn't Match Shift Criteria"
          description="The selected professional does not match the required license type. Please verify the profession type and candidate before proceeding."
          rightButtonText="Close"
          rightButtonAction={() => setShowProfessionMismatchWarning(false)}
          closePopup={() => setShowProfessionMismatchWarning(false)}
          dataTestId="profession-mismatch-popup"
          descriptionStyle={{
            width: '85%',
            marginBottom: 32,
          }}
        />
      )}
    </Column>
  );
};
